import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { newId } from '@gk/contracts'
import { roleLabel, useRoles, type RoleRow } from '../data/people.ts'
import { titleInRotation } from '../data/rotation.ts'
import { codeFrom, reorder, titleText, useJobTitles, type JobTitleRow } from '../data/staff.ts'
import { BackLink, Banner, Field, PendingBanner, Pill } from '../ui/components.tsx'
import { useWorkshopAction, useWriteAccess } from '../ui/hooks.ts'
import { Picto } from '../ui/pictos.tsx'

/** Réglages › Fonctions (M20 decision 1): the club's job titles, in the order of the employee list, and the access role each gives. job_title.define@1 is an office
 *  intent (person.manage, online, sendAndWait, no projector); changing a fonction's access role moves nobody's roles, it applies to the next hire or change.
 *  M23 item 28: each fonction says whether its people are in the rotation (a switch, job_title.define@2; by default every fonction but Superintendent and
 *  Mécanicien, "(à confirmer)": the owner's question 4); a row the device has not pulled since the column came shows the rule by code (`titleInRotation`). */
export function JobTitlesPage() {
  const { t, i18n } = useTranslation()
  const titles = useJobTitles(); const roles = useRoles()
  const manage = useWriteAccess('person.manage')
  const move = useWorkshopAction()
  if (!titles || !roles) return <p>{t('common.loading')}</p>
  const grantable = roles.filter((r) => r.code !== 'kiosk')
  const shift = async (id: string, dir: -1 | 1) => {
    for (const m of reorder(titles, id, dir)) {
      if (!(await move.run('job_title.define@1', { job_title_id: m.title.id, expected_revision: m.title.revision ?? 1, sort_order: m.sort_order }, 'jobTitles.moved'))) return
    }
  }
  return (
    <section className="flex flex-col gap-4" data-testid="job-titles-page">
      <BackLink to="/settings">← {t('common.back')}</BackLink>
      <h1 className="flex items-center gap-2 text-2xl font-bold"><Picto name="badge" className="size-7 text-brand dark:text-fairway" />{t('jobTitles.title')}</h1>
      <p className="meta">{t('jobTitles.help')}</p>
      <p className="meta" data-testid="jt-rotation-help">{t('jobTitles.rotationHelp')}</p>
      {manage.readOnlyMode && <Banner tone="warn">{t('common.onlineOnly')}</Banner>}
      {move.pending && <PendingBanner />}
      {move.error && <Banner tone="error">{move.error}</Banner>}
      {move.ok && <Banner tone="ok">{move.ok}</Banner>}
      <ul className="flex flex-col gap-3" data-testid="job-title-list">
        {titles.map((x, i) => (
          <TitleRow key={x.id} title={x} roles={grantable} canEdit={manage.allowed} lang={i18n.language}
            first={i === 0} last={i === titles.length - 1} moving={move.busy} onMove={(dir) => void shift(x.id, dir)} />
        ))}
      </ul>
      {manage.allowed && <NewTitle roles={grantable} />}
    </section>
  )
}

function TitleRow({ title, roles, canEdit, lang, first, last, moving, onMove }: {
  title: JobTitleRow; roles: readonly RoleRow[]; canEdit: boolean; lang: string; first: boolean; last: boolean; moving: boolean; onMove: (dir: -1 | 1) => void
}) {
  const { t } = useTranslation()
  const w = useWorkshopAction()
  const [editing, setEditing] = useState(false)
  const [f, setF] = useState({ fr: title.label.fr, en: title.label.en, role: title.access_role_code })
  const inRotation = titleInRotation(title)
  // a change kept on the device (no network) holds the switch until the row comes back with its new revision: a second tap would queue a second
  // define@2 with the same expected_revision, a REVISION_CONFLICT at the next sync for a change that did apply (review of item 10)
  useEffect(() => { if (w.pending) w.reset() }, [title.revision])                                        // eslint-disable-line react-hooks/exhaustive-deps
  const changed = f.fr.trim() !== title.label.fr || f.en.trim() !== title.label.en || f.role !== title.access_role_code
  const base = { job_title_id: title.id, expected_revision: title.revision ?? 1 }
  const name = titleText(title.label, lang)
  return (
    <li className="card flex flex-col gap-3" data-testid="job-title-row" data-code={title.code}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex flex-wrap items-center gap-2">
          <span className="text-lg font-bold">{name}</span>
          <span className="meta font-mono">{title.code}</span>
          {title.archived_at && <Pill tone="gray">{t('jobTitles.archived')}</Pill>}
        </span>
        {canEdit && (
          <span className="flex gap-1">
            <button className="btn min-w-14 !px-3" disabled={first || moving || w.busy} aria-label={t('jobTitles.up', { name })} onClick={() => onMove(-1)} data-testid="jt-up">↑</button>
            <button className="btn min-w-14 !px-3" disabled={last || moving || w.busy} aria-label={t('jobTitles.down', { name })} onClick={() => onMove(1)} data-testid="jt-down">↓</button>
          </span>
        )}
      </div>
      <p className="text-sm" data-testid="jt-access">{t('staff.hire.access', { role: roleLabel(roles as RoleRow[], title.access_role_code, lang) })}</p>
      {/* M23 item 28: in the rotation or not; a tap sends job_title.define@2 with the revision read (a 56-px switch, the state said in words) */}
      {canEdit ? (
        <button type="button" role="switch" aria-checked={inRotation} className="btn w-full justify-between sm:w-auto" disabled={w.busy || moving || w.pending}
          onClick={() => void w.run('job_title.define@2', { ...base, in_rotation: !inRotation }, 'jobTitles.rotationSaved')} data-testid="jt-rotation" data-in-rotation={inRotation}>
          <span>{t('jobTitles.rotation')}</span><Pill tone={inRotation ? 'green' : 'gray'}>{inRotation ? t('common.yes') : t('common.no')}</Pill>
        </button>
      ) : <p className="flex items-center gap-2 text-sm" data-testid="jt-rotation" data-in-rotation={inRotation}>{t('jobTitles.rotation')}<Pill tone={inRotation ? 'green' : 'gray'}>{inRotation ? t('common.yes') : t('common.no')}</Pill></p>}
      {title.archived_at && <p className="meta">{t('jobTitles.archivedHelp')}</p>}
      {!editing && w.ok && <Banner tone="ok">{w.ok}</Banner>}
      {!editing && w.pending && <PendingBanner />}
      {canEdit && !editing && <button className="btn self-start" onClick={() => { w.reset(); setF({ fr: title.label.fr, en: title.label.en, role: title.access_role_code }); setEditing(true) }} data-testid="jt-edit">{t('common.edit')}</button>}
      {canEdit && editing && (
        <form className="flex flex-col gap-3" onSubmit={(e) => {
          e.preventDefault()
          void w.run('job_title.define@1', { ...base, label: { fr: f.fr.trim(), en: f.en.trim() }, access_role_code: f.role }, 'jobTitles.saved').then((ok) => { if (ok) setEditing(false) })
        }}>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('jobTitles.fr')} htmlFor={`jt-fr-${title.id}`}><input id={`jt-fr-${title.id}`} className="input" maxLength={60} required value={f.fr} onChange={(e) => setF({ ...f, fr: e.target.value })} data-testid="jt-fr" /></Field>
            <Field label={t('jobTitles.en')} htmlFor={`jt-en-${title.id}`}><input id={`jt-en-${title.id}`} className="input" maxLength={60} required value={f.en} onChange={(e) => setF({ ...f, en: e.target.value })} data-testid="jt-en" /></Field>
          </div>
          <Field label={t('jobTitles.role')} htmlFor={`jt-role-${title.id}`}>
            <select id={`jt-role-${title.id}`} className="input" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="jt-role">
              {roles.map((r) => <option key={r.code} value={r.code}>{roleLabel(roles as RoleRow[], r.code, lang)}</option>)}
            </select>
          </Field>
          {w.pending && <PendingBanner />}
          {w.error && <Banner tone="error">{w.error}</Banner>}
          <div className="flex flex-wrap gap-2">
            <button className="btn btn-primary" disabled={w.busy || !changed || !f.fr.trim() || !f.en.trim()} data-testid="jt-save">{t('jobTitles.save')}</button>
            <button type="button" className="btn" disabled={w.busy} data-testid="jt-archive"
              onClick={() => void w.run('job_title.define@1', { ...base, archived: !title.archived_at }, 'jobTitles.saved').then((ok) => { if (ok) setEditing(false) })}>{title.archived_at ? t('jobTitles.unarchive') : t('jobTitles.archive')}</button>
            <button type="button" className="btn" disabled={w.busy} onClick={() => { setEditing(false); setF({ fr: title.label.fr, en: title.label.en, role: title.access_role_code }) }}>{t('common.cancel')}</button>
          </div>
        </form>
      )}
    </li>
  )
}

function NewTitle({ roles }: { roles: readonly RoleRow[] }) {
  const { t, i18n } = useTranslation()
  const w = useWorkshopAction()
  const id = useRef(newId())
  const [f, setF] = useState({ fr: '', en: '', code: '', role: 'crew' })
  const [codeTouched, setCodeTouched] = useState(false)
  const code = codeTouched ? f.code : codeFrom(f.fr)
  const ok = f.fr.trim() && f.en.trim() && /^[a-z0-9][a-z0-9_]{0,39}$/.test(code) && f.role
  return (
    <form className="card flex flex-col gap-3" data-testid="job-title-new" onSubmit={(e) => {
      e.preventDefault()
      void w.run('job_title.define@1', { job_title_id: id.current, code, label: { fr: f.fr.trim(), en: f.en.trim() }, access_role_code: f.role }, 'jobTitles.added')
        .then((done) => { if (done) { id.current = newId(); setF({ fr: '', en: '', code: '', role: 'crew' }); setCodeTouched(false) } })
    }}>
      <h2 className="text-lg font-bold">{t('jobTitles.new')}</h2>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('jobTitles.fr')} htmlFor="jt-new-fr"><input id="jt-new-fr" className="input" maxLength={60} value={f.fr} onChange={(e) => setF({ ...f, fr: e.target.value })} data-testid="jt-new-fr" /></Field>
        <Field label={t('jobTitles.en')} htmlFor="jt-new-en"><input id="jt-new-en" className="input" maxLength={60} value={f.en} onChange={(e) => setF({ ...f, en: e.target.value })} data-testid="jt-new-en" /></Field>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('jobTitles.code')} hint={t('jobTitles.codeHint')} htmlFor="jt-new-code">
          <input id="jt-new-code" className="input font-mono" maxLength={40} value={code} onChange={(e) => { setCodeTouched(true); setF({ ...f, code: e.target.value.toLowerCase() }) }} data-testid="jt-new-code" />
        </Field>
        <Field label={t('jobTitles.role')} htmlFor="jt-new-role">
          <select id="jt-new-role" className="input" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="jt-new-role">
            {roles.map((r) => <option key={r.code} value={r.code}>{roleLabel(roles as RoleRow[], r.code, i18n.language)}</option>)}
          </select>
        </Field>
      </div>
      {w.ok && <Banner tone="ok"><span data-testid="jt-new-ok">{w.ok}</span></Banner>}
      {w.pending && <PendingBanner />}
      {w.error && <Banner tone="error">{w.error}</Banner>}
      <button className="btn btn-primary self-start" disabled={w.busy || !ok} data-testid="jt-new-go">{t('jobTitles.add')}</button>
    </form>
  )
}
